import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
@Injectable({
  providedIn: 'root'
})

/**
 * @author Opeoluwa
 * @dateCreated 2nd Aug 2023
 * @description This service handles user authentication and permission management.
 * @dependencies The service depends on the `HttpClient` module from Angular's `@angular/common/http` for making HTTP requests.
 * @modifiedBy - Opeoluwa
 * @dateModified - 4th Aug 2023
 * @reasonForModification  - To store Data correctly
 */
export class AuthenticationService {
  private authTokenKey = 'authToken';
  private userSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public user: Observable<any> = this.userSubject.asObservable();
  private apiUrl = environment.API_SERVER; // Update this with your API URL

  constructor(private http: HttpClient) { }

  /**
   * @author Opeoluwa
   * @dateCreated 3rd Aug 2023
   * @description The function performs user authentication by sending login credentials to the server.
   * @param credentials - An object containing the user's login credentials (email and password).
   * @returns An Observable that emits a boolean value indicating the success or failure of the login attempt.
   * If the login is successful, it returns `true`, otherwise `false`.
   * @dependencies The function depends on the `HttpClient` service from Angular's `@angular/common/http` module
   * to make the HTTP POST request to the server for user authentication.
   * @modifiedBy Opeoluwa
   * @dateModified 5th Aug 2023
   * @reasonForModification - To store user details in the local storage
   */

  login(credentials: { email: string; password: string }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/signin`, credentials)
      .pipe(
        map((response) => {
          if (response && response.token) {
            // Store the token in localStorage
            localStorage.setItem(this.authTokenKey, response.token);

            // Store the user data in localStorage
            localStorage.setItem('userData', JSON.stringify(response.user));

            // Update the userSubject with the user data
            this.userSubject.next(response.user);

            return true; // Return true for successful login
          }
          return false; // Return false for failed login
        }),
        catchError((error: HttpErrorResponse) => {
          let errorMessage = 'An unknown error occurred. Please try again later.';
          if (error.status === 400) {
            errorMessage = 'Invalid email or password. Please check your credentials and try again.';
          } else if (error.status === 401) {
            errorMessage = error.error.message || 'Unauthorized: You do not have permission to access this resource.';
          } else if (error.status === 404) {
            errorMessage = 'API endpoint not found. Please check the server URL.';
          } // Add more error handling as needed

          return throwError(errorMessage);
        })
      );
  }

  /**
  * @author Opeoluwa
  * @dateCreated 3rd Aug 2023
  * @description The function logs out the currently authenticated user.
  * @returns None.
  * @dependencies None.
  * @modifiedBy - None
  * @dateModified - None
  * @reasonForModification - None
  */
  logout(): void {
    // Clear user data and token on logout
    this.clearUserData();
    this.userSubject.next(null);
  }
  /**
   * @author Opeoluwa
   * @dateCreated 3rd Aug 2023
   * @description The function clears the user data and authentication token from the local storage.
   * @returns None.
   * @dependencies None.
   * @modifiedBy None.
   * @dateModified -None.
   * @reasonForModification - None.
   */
  clearUserData(): void {
    // Clear user data and token from localStorage
    localStorage.removeItem(this.authTokenKey);
  }


  /**
  * @author Opeoluwa
   * @dateCreated 4th Aug 2023
   * @description The function returns the currently authenticated user's data.
   * @returns An object representing the currently authenticated user's data or `null` if the user is not authenticated.
   * @dependencies None.
   * @modifiedBy - None.
   * @dateModified -None
   * @reasonForModification - None
   */
  getUser(): any {
    return this.userSubject.value;
  }


  /**
   * @author Opeoluwa
  * @dateCreated 4th Aug 2023
  * @description The function checks if the currently authenticated user has a specific permission.
  * @param permission - A string representing the permission to check.
  * @returns `true` if the user has the specified permission, otherwise `false`.
  * @dependencies None.
  * @modifiedBy -None.
  * @dateModified -None.
  * @reasonForModification -None.
  */

  hasPermission(permission: string): boolean {
    // Check if the user has a specific permission
    const user = this.getUser();
    return user && user.role && user.role.permissions.includes(permission);
  }

  /**
   * @author Opeoluwa
  * @dateCreated 4th Aug 2023
  * @description The function checks if there is an authenticated user.
  * @returns `true` if a user is authenticated, otherwise `false`.
  * @dependencies None.
  * @modifiedBy - None
  * @dateModified - None
  * @reasonForModification - None
  */
  isAuthenticated(): boolean {
    return !!localStorage.getItem(this.authTokenKey);
  }

  /**
    * @author Opeoluwa
 * @dateCreated 4th Aug 2023
 * @description The function checks if there is an authenticated user.
 * @returns `true` if a user is authenticated, otherwise `false`.
 * @dependencies None.
 * @modifiedBy - None
 * @dateModified - None
 * @reasonForModification - None
 */
  getAuthToken(): string | null {
    return localStorage.getItem(this.authTokenKey);
  }



  // Unsubscribe
}
